/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    Diff,
    Hunk,
    withSourceExpansion,
    minCollapsedLines,
    tokenize,
    markEdits,
} from "react-diff-view"
import HunkInfo from "./HunkInfo"
import UnfoldCollapsed from "./UnfoldCollapsed"
import "./DiffView.scss"
import * as React from "react"
import { Symbols } from "@tenforce/toolbox-fontmap"
import bem from "../../bem"

const b = bem.with("NewUI__packageImport__diffView")

interface Props {
    change: FileDiff.Change
    hunks: FileDiff.Hunk[]
    oldSource: string
    type: string
    showGutter: boolean
    selectedChanges?: FileDiff.Change[]
    onExpandRange: (start: number, end: number) => void
}

interface State {
    opened: boolean
}

class DiffView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            opened: true,
        }
    }

    render(): JSX.Element {
        const {
            change,
            hunks,
            oldSource,
            type,
            showGutter,
            selectedChanges,
            onExpandRange,
        } = this.props

        const renderHunks = hunkRenderer(oldSource, onExpandRange)

        const fileNameHeader = buildFileNameHeader(change)

        const tokens = tokenizeHunks(oldSource, hunks)

        return (
            <div className={b()}>
                <div className={b("fileNameHeader")} onClick={this.toggleOpen}>
                    {this.state.opened ? Symbols.ExpandedBelow : Symbols.Expand}{" "}
                    {fileNameHeader}
                </div>
                {this.state.opened ? (
                    <Diff
                        viewType="split"
                        diffType={type}
                        hunks={hunks}
                        tokens={tokens}
                        oldSource={oldSource}
                        gutterType={showGutter ? "default" : "none"}
                        selectedChanges={selectedChanges}
                        optimizeSelection={true}
                    >
                        {renderHunks}
                    </Diff>
                ) : (
                    <div className={b("collapsed")}>
                        This diff is collapsed.
                    </div>
                )}
            </div>
        )
    }

    toggleOpen = (): void => {
        this.setState({
            opened: !this.state.opened,
        })
    }
}

const tokenizeHunks = (
    oldSource: string,
    hunks: FileDiff.Hunk[],
): FileDiff.Token => {
    const options = {
        highlight: false,
        oldSource: oldSource,
        enhancers: [markEdits(hunks)],
    }

    return tokenize(hunks, options)
}

const hunkRenderer = (
    oldSource: string,
    onExpandRange: (start: number, end: number) => void,
): ((hunks: FileDiff.Hunk[]) => JSX.Element[]) => {
    const linesCount: number = oldSource ? oldSource.split("\n").length : 0
    const renderHunk = (
        children: JSX.Element[],
        hunk: FileDiff.Hunk,
        i: number,
        hunks: FileDiff.Hunk[],
    ): JSX.Element[] => {
        const previousElement = children[children.length - 1]
        const decorationElement = oldSource ? (
            <UnfoldCollapsed
                key={"decoration-" + hunk.content}
                previousHunk={previousElement && previousElement.props.hunk}
                currentHunk={hunk}
                linesCount={linesCount}
                onExpand={onExpandRange}
            />
        ) : (
            <HunkInfo key={"decoration-" + hunk.content} hunk={hunk} />
        )
        children.push(decorationElement)

        const hunkElement = <Hunk key={"hunk-" + hunk.content} hunk={hunk} />
        children.push(hunkElement)

        if (i === hunks.length - 1 && oldSource) {
            const unfoldTailElement = (
                <UnfoldCollapsed
                    key="decoration-tail"
                    previousHunk={hunk}
                    linesCount={linesCount}
                    onExpand={onExpandRange}
                />
            )
            children.push(unfoldTailElement)
        }

        return children
    }
    const renderHunks = (hunks: FileDiff.Hunk[]): JSX.Element[] =>
        hunks.reduce(renderHunk, [])
    return renderHunks
}

const buildFileNameHeader = (change: FileDiff.Change): string => {
    const fileNameHeader: string = change.kind + " "
    if (!change.oldFileName && !change.newFileName) {
        throw new Error("No filenames found in change")
    } else if (change.oldFileName && !change.newFileName) {
        return fileNameHeader + change.oldFileName
    } else if (!change.oldFileName && change.newFileName) {
        return fileNameHeader + change.newFileName
    } else if (change.oldFileName === change.newFileName) {
        return fileNameHeader + change.newFileName
    } else if (change.oldFileName !== change.newFileName) {
        return fileNameHeader + change.oldFileName + " → " + change.newFileName
    } else {
        throw new Error("Error building fileNameHeader")
    }
}

const compose = (...funcs: any[]): any =>
    funcs.reduce(
        (a: any, b: any) =>
            (...args: any[]): boolean =>
                a(b(...args)),
        (arg: any) => arg,
    )

const enhance = compose(withSourceExpansion(), minCollapsedLines(5))

export default enhance(DiffView)
